import { IconHeart, IconUsers, IconLeaf, IconFlame, IconScale, IconStar, IconMountain, IconShield } from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';

export interface ThemeConfig {
  slug: string;
  label: string;
  color: string; // Mantine color name
  icon: Icon;
  emoji?: string;
}

/**
 * Centralized theme configuration for consistent UI across the app.
 * This maps theme slugs to their visual properties (color, icon, emoji).
 *
 * Colors use Mantine's color system:
 * - blue, cyan, teal, green, lime, yellow, orange, red, pink, grape, violet, indigo, gray
 */
export const THEME_CONFIG: Record<string, ThemeConfig> = {
  // Core themes matching database slugs
  'walking-in-their-shoes': {
    slug: 'walking-in-their-shoes',
    label: 'Walking in Their Shoes',
    color: 'pink',
    icon: IconHeart,
    emoji: '💗',
  },
  'dream-big-stay-kind': {
    slug: 'dream-big-stay-kind',
    label: 'Dream Big, Stay Kind',
    color: 'teal',
    icon: IconUsers,
    emoji: '🤝',
  },
  'who-am-i': {
    slug: 'who-am-i',
    label: 'Who Am I?',
    color: 'grape',
    icon: IconLeaf,
    emoji: '🍂',
  },
  'hidden-superpowers': {
    slug: 'hidden-superpowers',
    label: 'Hidden Superpowers',
    color: 'violet',
    icon: IconShield,
    emoji: '🛡️',
  },
  'when-things-change': {
    slug: 'when-things-change',
    label: 'When Things Change',
    color: 'orange',
    icon: IconLeaf,
    emoji: '🍃',
  },
  'tough-choices': {
    slug: 'tough-choices',
    label: 'Tough Choices',
    color: 'blue',
    icon: IconScale,
    emoji: '⚖️',
  },
  'being-brave': {
    slug: 'being-brave',
    label: 'Being Brave',
    color: 'orange',
    icon: IconFlame,
    emoji: '🔥',
  },
};

/**
 * Default theme configuration for unknown or missing themes.
 */
const DEFAULT_THEME: ThemeConfig = {
  slug: 'unknown',
  label: 'Unknown Theme',
  color: 'gray',
  icon: IconStar,
};

/**
 * Get theme configuration by slug or label (case-insensitive).
 * Falls back to a default gray theme if not found.
 */
export function getThemeConfig(themeIdentifier?: string | null): ThemeConfig {
  // Handle null, undefined, or empty string
  if (!themeIdentifier) {
    return DEFAULT_THEME;
  }

  const normalized = themeIdentifier.toLowerCase().trim();

  // Try exact slug match first
  if (THEME_CONFIG[normalized]) {
    return THEME_CONFIG[normalized];
  }

  // Try to find by label match (exact)
  const byLabel = Object.values(THEME_CONFIG).find(
    (config) => config.label.toLowerCase() === normalized
  );
  if (byLabel) return byLabel;

  // Try to find by label prefix match (for themes like "Dream Big, Stay Kind (ambition and humility)")
  const byLabelPrefix = Object.values(THEME_CONFIG).find(
    (config) => normalized.startsWith(config.label.toLowerCase())
  );
  if (byLabelPrefix) return byLabelPrefix;

  // Try to find by partial match (for theme names that contain the slug)
  const byPartialMatch = Object.values(THEME_CONFIG).find(
    (config) => normalized.includes(config.slug) || config.slug.includes(normalized)
  );
  if (byPartialMatch) return byPartialMatch;

  // Default fallback with original theme name
  return {
    slug: 'unknown',
    label: themeIdentifier,
    color: 'gray',
    icon: IconStar,
  };
}

/**
 * Get all theme configurations as an array, useful for rendering lists.
 */
export function getAllThemes(): ThemeConfig[] {
  return Object.values(THEME_CONFIG);
}
